import { useEffect, useId, useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import type { CrmRecord, SchemaField, SchemaObject } from '../../lib/api-types';
import { useCreateRecord, useUpdateRecord } from '../../lib/queries';
import { ApiError } from '../../lib/api-client';
import { Icon } from '../ui/Icon';
import { MarkdownEditor } from '../content/MarkdownContent';
import styles from './Crm.module.css';

export function editableFields(object: SchemaObject) {
  // isSystem marks seeded fields too (including note title/body); it does not
  // mean read-only. Actor and position values are managed by the application.
  return object.fields.filter(field => !['relation', 'actor', 'position'].includes(field.type))
    .sort((a, b) => Number(b.name === object.labelField) - Number(a.name === object.labelField) || a.position - b.position);
}

type Draft = Record<string, unknown>;
function initialDraft(fields: SchemaField[], record?: CrmRecord): Draft {
  return Object.fromEntries(fields.map(field => [field.name,
    record?.data[field.name] ?? record?.fields.find(item => item.name === field.name)?.value ?? null,
  ]));
}

export function recordPatch(fields: SchemaField[], draft: Draft, initial: Draft, creating: boolean): Draft {
  const patch: Draft = {};
  for (const field of fields) {
    let value = draft[field.name];
    if (JSON.stringify(value) === JSON.stringify(initial[field.name])) {
      if (creating && field.isRequired) throw new Error(`${field.label} is required.`);
      continue;
    }
    if (value === '' || value == null) value = null;
    if (field.isRequired && (value === null || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && !value.length))) {
      throw new Error(`${field.label} is required.`);
    }
    if (value !== null && ['number', 'rating'].includes(field.type)) {
      value = Number(value);
      if (!Number.isFinite(value)) throw new Error(`${field.label} must be a number.`);
    }
    if (value !== null && field.type === 'datetime') value = new Date(String(value)).toISOString();
    if (value !== null && field.type === 'currency') {
      const currency = value as { amount: unknown; currency: string };
      if (currency.amount === '' || currency.amount == null || !Number.isFinite(Number(currency.amount))) throw new Error(`${field.label} needs an amount.`);
      value = { ...currency, amount: Number(currency.amount) };
    }
    patch[field.name] = value;
  }
  return patch;
}

export function RecordEditor({ object, version, record, onClose, onSaved }: {
  object: SchemaObject; version: number; record?: CrmRecord; onClose: () => void; onSaved: (id: string) => void;
}) {
  const fields = editableFields(object);
  const [initial] = useState(() => initialDraft(fields, record));
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState('');
  const create = useCreateRecord(version, object.namePlural);
  const update = useUpdateRecord(version, object.namePlural);
  const saving = create.isPending || update.isPending;
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const saveLock = useRef(false);
  const saved = useRef(false);

  useBlocker({
    disabled: !dirty,
    enableBeforeUnload: () => dirty && !saved.current,
    shouldBlockFn: () => !saved.current && (saveLock.current || !window.confirm('Discard your unsaved changes?')),
  });

  useEffect(() => {
    const element = dialog.current!;
    const previous = document.activeElement as HTMLElement | null;
    element.showModal();
    element.querySelector<HTMLElement>('input, textarea, select')?.focus();
    return () => { element.close(); previous?.focus(); };
  }, []);

  const close = () => {
    if (!saving && (!dirty || window.confirm('Discard your unsaved changes?'))) onClose();
  };
  const save = async () => {
    if (saveLock.current) return;
    saveLock.current = true;
    setError('');
    try {
      const data = recordPatch(fields, draft, initial, !record);
      const result = record ? await update.mutateAsync({ id: record.id, data }) : await create.mutateAsync(data);
      saved.current = true;
      onSaved(result.record.id);
    } catch (cause) {
      const issues = cause instanceof ApiError && cause.code === 'record_validation_failed'
        ? (cause.details as { issues?: Array<{ field: string; message: string }> } | undefined)?.issues : undefined;
      setError(issues?.length ? issues.map(issue => `${fields.find(field => field.name === issue.field)?.label ?? issue.field}: ${issue.message}`).join(' ')
        : cause instanceof Error ? cause.message : 'Could not save. Try again.');
    } finally { saveLock.current = false; }
  };

  return <dialog ref={dialog} className={styles.editorDialog} aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); close(); }}>
    <form className={styles.editorForm} onSubmit={event => { event.preventDefault(); void save(); }}
      onKeyDown={event => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault();
          if (!saving) event.currentTarget.requestSubmit();
        }
      }}>
      <header className={styles.editorHeader}>
        <h2 id={titleId}>{record ? 'Edit' : 'New'} {object.label}</h2>
        <button type="button" className={styles.action} disabled={saving} onClick={close} aria-label="Close editor"><Icon name="x" size={18} /></button>
      </header>
      <fieldset className={styles.editorFields} disabled={saving}>
        {fields.map(field => <EditorField key={field.id} field={field} value={draft[field.name]}
          onChange={value => setDraft(current => ({ ...current, [field.name]: value }))} />)}
      </fieldset>
      {error && <p role="alert" className={styles.editorError}>{error}</p>}
      <footer className={styles.editorFooter}>
        <button type="button" className={styles.action} disabled={saving} onClick={close}>Cancel</button>
        <button type="submit" className={`${styles.action} ${styles.actionPrimary}`} disabled={saving || (Boolean(record) && !dirty)}>
          {saving ? 'Saving…' : record ? 'Save changes' : `Create ${object.label}`}
        </button>
      </footer>
    </form>
  </dialog>;
}

function choices(field: SchemaField): Array<{ value: string; label: string }> {
  return ((field.options.choices ?? []) as Array<string | { value: string; label?: string }>).map(choice =>
    typeof choice === 'string' ? { value: choice, label: choice } : { value: choice.value, label: choice.label ?? choice.value });
}

function EditorField({ field, value, onChange }: { field: SchemaField; value: unknown; onChange: (value: unknown) => void }) {
  const id = useId();
  const label = `${field.label}${field.isRequired ? ' *' : ''}`;
  const input = { id, required: field.isRequired, 'aria-label': field.label };
  let control;
  if (field.type === 'markdown') {
    control = <MarkdownEditor id={id} label={field.label} required={field.isRequired} value={String(value ?? '')} onChange={onChange} />;
  } else if (field.type === 'select' || field.type === 'boolean') {
    control = <select {...input} value={value == null ? '' : String(value)} onChange={event => onChange(event.target.value === '' ? null : field.type === 'boolean' ? event.target.value === 'true' : event.target.value)}>
      <option value="">Choose…</option>
      {(field.type === 'boolean' ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] : choices(field)).map(choice => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
    </select>;
  } else if (field.type === 'multiSelect') {
    control = <div className={styles.editorChoices}>{choices(field).map(choice => <label key={choice.value}>
      <input type="checkbox" checked={((value ?? []) as string[]).includes(choice.value)} onChange={event => onChange(event.target.checked
        ? [...((value ?? []) as string[]), choice.value] : (value as string[]).filter(item => item !== choice.value))} /> {choice.label}
    </label>)}</div>;
  } else if (field.type === 'currency') {
    const currency = (value ?? { amount: '', currency: field.options.defaultCurrency ?? 'USD' }) as { amount: string | number; currency: string };
    control = <div className={styles.editorCompound}>
      <input {...input} type="number" step="any" value={currency.amount} onChange={event => onChange({ ...currency, amount: event.target.value })} />
      <input aria-label={`${field.label} currency`} placeholder="USD" maxLength={3} pattern="[A-Z]{3}" value={currency.currency} onChange={event => onChange({ ...currency, currency: event.target.value.toUpperCase() })} />
    </div>;
  } else if (field.type === 'address') {
    const address = (value ?? {}) as Record<string, string>;
    control = <div className={styles.editorCompound}>{[
      ['line1', 'Street'], ['line2', 'Apartment / suite'], ['city', 'City'], ['region', 'State / region'], ['postalCode', 'Postal code'], ['country', 'Country'],
    ].map(([key, part]) => <input key={key!} aria-label={`${field.label} ${part}`} placeholder={part} value={address[key!] ?? ''}
      onChange={event => onChange({ ...address, [key!]: event.target.value })} />)}</div>;
  } else if (field.type === 'emails' || field.type === 'phones') {
    const key = field.type === 'emails' ? 'address' : 'number';
    const points = (value ?? []) as Array<Record<string, unknown>>;
    control = <div className={styles.editorChoices}>
      {points.map((point, index) => <div className={styles.editorCompound} key={index}>
        <input type={key === 'address' ? 'email' : 'tel'} aria-label={`${field.label} ${index + 1}`} required
          value={String(point[key] ?? '')} onChange={event => onChange(points.map((item, i) => i === index ? { ...item, [key]: event.target.value } : item))} />
        <input aria-label={`${field.label} ${index + 1} label`} placeholder="Label" value={String(point.label ?? '')}
          onChange={event => onChange(points.map((item, i) => i === index ? { ...item, label: event.target.value } : item))} />
        <button type="button" className={styles.action} aria-label={`Remove ${field.label} ${index + 1}`} onClick={() => onChange(points.filter((_, i) => i !== index))}>Remove</button>
      </div>)}
      <button type="button" className={styles.action} onClick={() => onChange([...points, { [key]: '' }])}>Add {field.label}</button>
    </div>;
  } else {
    const type = field.type === 'datetime' ? 'datetime-local' : ['number', 'rating'].includes(field.type) ? 'number' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text';
    let text = String(value ?? '');
    if (field.type === 'datetime' && text) {
      const date = new Date(text);
      if (!Number.isNaN(date.getTime())) text = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
    control = <input {...input} type={type} step={field.type === 'rating' ? '1' : 'any'}
      min={field.type === 'rating' ? 0 : undefined} max={field.type === 'rating' ? Number(field.options.max ?? 5) : undefined}
      value={text} onChange={event => onChange(event.target.value)} />;
  }
  return <div className={styles.editorField}>
    <label htmlFor={id}>{label}</label>{control}
    {!field.isRequired && value != null && <button type="button" className={styles.clearField} onClick={() => onChange(null)}>Clear {field.label}</button>}
    {field.description && <small>{field.description}</small>}
  </div>;
}
